import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Ban, ExternalLink, Search, ShieldCheck } from 'lucide-react';
import { Button, Card } from '../components';
import { adminIntrekken, adminLijst, AdminPaspoort } from '../services/kwaliteitspaspoortApi';

/**
 * Admin: uitgegeven Kwaliteitspaspoorten bekijken en intrekken (migratie 152).
 * Server: /api/admin/kwaliteitspaspoorten. De reden blijft intern; de publieke
 * verificatiepagina toont alleen "ingetrokken door Belagom".
 */

const fmtGetal = (n: number) => n.toLocaleString('nl-NL', { maximumFractionDigits: 1 });
const fmtDag = (iso: string) =>
  new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });

const AdminKwaliteitspaspoortenPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [invoer, setInvoer] = useState('');
  const [zoek, setZoek] = useState('');
  const [intrekken, setIntrekken] = useState<AdminPaspoort | null>(null);
  const [reden, setReden] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setZoek(invoer.trim()), 300);
    return () => clearTimeout(id);
  }, [invoer]);

  const lijst = useQuery({ queryKey: ['admin-kwaliteitspaspoorten', zoek], queryFn: () => adminLijst(zoek || undefined) });

  const trekIn = useMutation({
    mutationFn: (v: { id: string; reden: string }) => adminIntrekken(v.id, v.reden),
    onSuccess: () => {
      toast.success(t('adminPaspoorten.revoked'));
      setIntrekken(null);
      setReden('');
      queryClient.invalidateQueries({ queryKey: ['admin-kwaliteitspaspoorten'] });
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message || t('adminPaspoorten.error')),
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button type="button" onClick={() => navigate('/admin')} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> {t('adminPaspoorten.back')}
        </button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[#0B1F3A]" /> {t('adminPaspoorten.title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1 mb-6">{t('adminPaspoorten.subtitle')}</p>

        <div className="relative mb-4 max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={invoer}
            onChange={(e) => setInvoer(e.target.value)}
            placeholder={t('adminPaspoorten.searchPh')}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm"
          />
        </div>

        <Card>
          {lijst.isLoading ? (
            <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" /></div>
          ) : lijst.isError ? (
            <p className="p-8 text-center text-sm text-red-600">{t('adminPaspoorten.error')}</p>
          ) : (lijst.data ?? []).length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">{t('adminPaspoorten.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
                    <th className="py-2 pr-3">{t('adminPaspoorten.colProfessional')}</th>
                    <th className="py-2 pr-3">{t('adminPaspoorten.colPeriod')}</th>
                    <th className="py-2 pr-3 text-right">PE</th>
                    <th className="py-2 pr-3 text-right">{t('adminPaspoorten.colIntervision')}</th>
                    <th className="py-2 pr-3 text-right">{t('adminPaspoorten.colPractice')}</th>
                    <th className="py-2 pr-3">{t('adminPaspoorten.colStatus')}</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {(lijst.data ?? []).map((p) => (
                    <tr key={p.id} className="border-b border-gray-50 align-top">
                      <td className="py-2.5 pr-3">
                        <p className="font-medium text-gray-900">{p.professional.naam}</p>
                        <p className="text-xs text-gray-500">{p.professional.email}</p>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700">
                        {fmtDag(p.periode_start)} – {fmtDag(p.periode_eind)}
                        <p className="text-xs text-gray-400">{t('adminPaspoorten.issuedOn', { date: fmtDag(p.created_at) })}</p>
                      </td>
                      <td className="py-2.5 pr-3 text-right">{fmtGetal(p.pe_punten)}</td>
                      <td className="py-2.5 pr-3 text-right">{fmtGetal(p.intervisie_uren)} u</td>
                      <td className="py-2.5 pr-3 text-right">{fmtGetal(p.praktijk_uren)} u</td>
                      <td className="py-2.5 pr-3">
                        {p.ingetrokken_op ? (
                          <>
                            <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-600">
                              {t(p.ingetrokken_door === 'belagom' ? 'adminPaspoorten.revokedByBelagom' : 'adminPaspoorten.revokedByPro')}
                            </span>
                            {p.ingetrokken_reden && <p className="mt-1 text-xs text-gray-500 max-w-[16rem]">{p.ingetrokken_reden}</p>}
                          </>
                        ) : (
                          <span className="rounded bg-green-50 px-1.5 py-0.5 text-xs font-semibold text-green-700">{t('adminPaspoorten.valid')}</span>
                        )}
                      </td>
                      <td className="py-2.5 text-right whitespace-nowrap">
                        <a
                          href={p.verificatie_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#0B1F3A] hover:underline mr-3"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> {t('adminPaspoorten.verify')}
                        </a>
                        {!p.ingetrokken_op && (
                          <button
                            type="button"
                            onClick={() => setIntrekken(p)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"
                          >
                            <Ban className="w-3.5 h-3.5" /> {t('adminPaspoorten.revoke')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {intrekken && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h2 className="text-lg font-bold text-gray-900">{t('adminPaspoorten.revokeTitle')}</h2>
              <p className="mt-1 text-sm text-gray-600">
                {t('adminPaspoorten.revokeText', {
                  name: intrekken.professional.naam,
                  from: fmtDag(intrekken.periode_start),
                  to: fmtDag(intrekken.periode_eind),
                })}
              </p>
              <label className="mt-4 block text-sm font-medium text-gray-700">
                {t('adminPaspoorten.reason')}
                <textarea
                  value={reden}
                  onChange={(e) => setReden(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <p className="mt-1 text-xs text-gray-400">{t('adminPaspoorten.reasonHint')}</p>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setIntrekken(null); setReden(''); }}>{t('adminPaspoorten.cancel')}</Button>
                <Button
                  disabled={reden.trim().length < 5}
                  loading={trekIn.isPending}
                  onClick={() => trekIn.mutate({ id: intrekken.id, reden })}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {t('adminPaspoorten.revokeConfirm')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminKwaliteitspaspoortenPage;
